import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendSingleRequest } from '../../apis';
import {
  Button,
  CloseIcon,
  Map,
  ModalComponent,
  Spinner,
  Subtitle,
  Title,
} from '../../components/shared';
import {
  loadQueueData,
  loadSpatStorages,
} from '../../store/farms/farms.actions';
import { showFeedback } from '../../store/ui/ui.actions';
import { Modal } from 'antd';
import SeedingModal from '../../components/farm-modals/SeedingModal';
import MaintenanceModal from '../../components/farm-modals/MaintenanceModal';
import FloatManageModal from '../../components/farm-modals/FloatManageModal';
import AssessmentModal from '../../components/farm-modals/AssessmentModal';
import HarvestCompleteModal from '../../components/farm-modals/HarvestCompleteModal';
import { selectQueueData } from '../../store/farms/farms.selector';
import {
  IQueueItem,
  TValidationType,
} from '../../entities/automation.entities';
import PaperFormModal from './PaperFormModal';
import { useHistory } from 'react-router-dom';
import {
  IWeatherLocation,
  TBusinessType,
} from '../../entities/general.entities';
import BulkHarvestModal from '../../components/farm-modals/BulkHarvestModal';
import { TLang } from '../../entities/ui.entities';
import { translate } from '../../lib/lang.helper';
import { selectLang } from '../../store/ui/ui.selector';
import GrowerSeedModal from '../../components/farm-modals/GrowerSeedModal';
import GrowerHarvestModal from '../../components/farm-modals/GrowerHarvestModal';
import { SelectIsGrower } from '../../store/extra/extra.selector';
import CatchSpatModal from '../../components/farm-modals/CatchSpatModal';
import BulkSeedingModal from '../../components/farm-modals/BulkSeedingModal';
import { defaultDateFormat } from '../../lib/common.helpers';
import { loadInventories } from '../../store/inventories/inventories.actions';
import ReturnHarvestModal from '../../components/farm-modals/ReturnHarvestModal';
import LineTagModal from '../../components/automation/LineTagModal';
import { selectBoats } from '../../store/users/users.selector';
import { loadTags } from '../../store/automation/automation.actions';

const isUpdatable = (item: IQueueItem) =>
  item.type !== 'tagging' && !item.type.startsWith('paper_');

const WarningBox = ({
  onClick,
  lang,
}: {
  onClick: (x: any) => void;
  lang: TLang | undefined;
}) => (
  <>
    <div>{translate(lang, '_validate_action_warning')}</div>
    <div
      style={{
        textDecoration: 'underline',
        fontStyle: 'italic',
        cursor: 'pointer',
      }}
      onClick={onClick}
    >
      {translate(lang, '_validate_click_here')}
    </div>
  </>
);

interface MapProps {
  title: string;
  visible: boolean;
  location: IWeatherLocation;
  onClose: () => void;
}

const MapModal = ({ title, visible, onClose, location }: MapProps) => (
  <Modal
    visible={visible}
    onCancel={onClose}
    closable
    closeIcon={<CloseIcon />}
    width={600}
    footer={null}
  >
    <div className='wrap'>
      <div className='d-flex align-center mb-16'>
        <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
          {title}
        </Subtitle>
      </div>
      <div style={{ height: '600px' }}>
        <Map
          zoom={10}
          position={{ lat: location.latitude, lng: location.longitude }}
          defaultPosition={{
            lat: location.latitude,
            lng: location.longitude,
          }}
        />
      </div>
    </div>
  </Modal>
);

const titles = {
  assessment: 'Assessment data',
  maintenance: 'Maintenance data',
  seeding: 'Seed data',
  inventory_seed: 'Floats data',
  harvest: 'Harvest data',
  paper_seeding: 'Paper Seeding form',
  paper_floating: 'Paper Floating form',
  paper_assessment: 'Paper Assessment form',
  paper_harvest: 'Paper Harvest form',
  bulk_mussel_harvest: 'Bulk Mussel Harvests',
  bulk_oyster_harvest: 'Bulk Oyster Harvests',
  bulk_mussel_seed: 'Bulk Mussel Seeding',
  bulk_oyster_seed: 'Bulk Oyster Seeding',
  tagging: 'Line tagging',
};

interface IDetail {
  [key: string]: any;
  id: number;
  _type: TValidationType;
  crop_type: TBusinessType;
}

interface ItemProps {
  item: IQueueItem;
  disabled: boolean;
  onViewLocation: (location: IWeatherLocation) => void;
  onViewItem: (item: IQueueItem, isUpdate: boolean) => void;
  onDeleteItem: (data: { type: string; item_id: number }) => void;
}

const QueueItem = ({
  item,
  disabled,
  onViewLocation,
  onViewItem,
  onDeleteItem,
}: ItemProps) => {
  const lang = useSelector(selectLang);
  const boats = useSelector(selectBoats);
  const curBoat = boats.find(x => x.id === item.boat_id);

  const isBulkItem = item.type.startsWith('bulk_');
  const itemTitle =
    item.type === 'harvest' && item.is_waste
      ? 'Stripped to Waste'
      : titles[item.type] || item.type;
  const submittedInfo = `Submitted by ${item.user.name} at ${defaultDateFormat(
    item.created_at,
  )}`;

  const handleViewLocation = () => {
    if (item.weather_condition) {
      onViewLocation(item.weather_condition);
    }
  };

  const handleDelete = () =>
    onDeleteItem({
      type: item.type,
      item_id: item.id,
    });

  return (
    <div className='queue-item'>
      <div className='--queue-left'>
        {isBulkItem ? (
          <div className='--item-farm'>{`${item.lines_count} lines`}</div>
        ) : (
          <>
            <div className='--item-farm'>
              {`${translate(lang, 'Farm')}: ${item.farm.name}`}
            </div>
            <div className='--item-line'>
              {`${translate(lang, 'Line')}: ${item.line.line_name}`}
            </div>
            {curBoat && (
              <div className='--item-line'>
                {`${translate(lang, 'Boat')}: ${curBoat.name}`}
              </div>
            )}
          </>
        )}
      </div>

      <div className='--queue-middle'>
        <div className='--queue-item-type'>{translate(lang, itemTitle)}</div>
        <div className='--queue-item-author'>{submittedInfo}</div>
        {item.weather_condition && (
          <div style={{ textAlign: 'center', marginTop: 10 }}>
            <button
              className='view-location-btn'
              onClick={handleViewLocation}
              type='button'
            >
              {translate(lang, '_view_data_entry_location')}
            </button>
          </div>
        )}
      </div>

      <div className='--queue-right'>
        <Button
          color='green'
          size={0}
          width='small'
          type='fill'
          onClick={() => onViewItem(item, false)}
          disabled={disabled}
        >
          {translate(lang, 'View')}
        </Button>
        {isUpdatable(item) && (
          <Button
            className='mt-7'
            color='blue'
            size={0}
            width='small'
            type='fill'
            disabled={disabled}
            onClick={() => onViewItem(item, true)}
          >
            {translate(lang, 'Update')}
          </Button>
        )}
        <Button
          className='mt-7'
          color='red'
          size={0}
          width='small'
          type='fill'
          disabled={disabled}
          onClick={handleDelete}
        >
          {translate(lang, 'Delete')}
        </Button>
      </div>
    </div>
  );
};

const QueuedDataPage = () => {
  const dispatch = useDispatch<any>();
  const history = useHistory();
  const queueData = useSelector(selectQueueData);
  const lang = useSelector(selectLang);

  const [loading, setLoading] = useState(true);
  const [selectedData, setSelectedData] = useState<IDetail>();
  const [disable, setDisable] = useState(false);
  const [mapLoc, setMapLoc] = useState<IWeatherLocation>();
  const [deletingItem, setDeletingItem] = useState<{
    type: string;
    item_id: number;
  }>();
  const [updatingItem, setUpdatingItem] = useState<IQueueItem>();
  const [warning, setWarning] = useState<any>();
  const [showCustom, setShowCustom] = useState(false);

  const loadData = useCallback(
    async (formData: any = undefined) => {
      setLoading(true);
      await dispatch(loadQueueData());
      if (formData?.tagged_ids) {
        dispatch(loadTags());
      }
      setLoading(false);
    },
    [dispatch],
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const showSuccess = (message: string) =>
    dispatch(
      showFeedback({
        isMessage: true,
        type: 'success',
        message: translate(lang, message),
      }),
    );
  const showError = (message: string) =>
    dispatch(
      showFeedback({
        isMessageModal: true,
        type: 'error',
        message: translate(lang, message),
        duration: -1,
      }),
    );

  const confirmMaintenance = async (data: any) => {
    if (!selectedData) return;
    if (updatingItem !== undefined) {
      const res = await updateQueueItem(data, updatingItem.id, 'maintenance');
      if (res.status) {
        await loadData(data);
        setSelectedData(undefined);
        showSuccess(res.data?.message ?? 'Success');
      } else {
        showError(res.data?.message ?? 'Something went wrong');
      }
      return true;
    } else {
      setDisable(true);
      const res = await sendSingleRequest(
        {
          id: selectedData.id,
          ...data,
          weather_condition: selectedData.weather_condition,
        },
        'POST',
        'api/queue/confirm-maintenance',
        true,
      );
      setDisable(false);
      if (res.status) {
        await loadData(data);
        setSelectedData(undefined);
        showSuccess(res.data?.message ?? 'Success');
        return true;
      } else {
        return res.data;
      }
    }
  };
  const confirmAssessment = async (data: any) => {
    if (!selectedData) return;
    if (updatingItem) {
      const res = await updateQueueItem(data, updatingItem.id, 'assessment');
      if (res.status) {
        await loadData(data);
        setSelectedData(undefined);
        showSuccess(res.data?.message ?? 'Success');
      } else {
        showError(res.data?.message ?? 'Something went wrong');
      }
    } else {
      setDisable(true);
      const res = await sendSingleRequest(
        {
          id: selectedData.id,
          ...data,
          weather_condition: selectedData.weather_condition,
        },
        'POST',
        'api/queue/confirm-assessment',
        true,
      );
      if (res.status) {
        await loadData(data);
        setSelectedData(undefined);
        showSuccess(res.data?.message ?? 'Success');
      } else {
        showError(res.data?.message ?? 'Something went wrong');
      }
      setDisable(false);
    }
  };
  const confirmInventorySeed = async (data: any) => {
    if (!selectedData) return;
    if (updatingItem) {
      const res = await updateQueueItem(
        data,
        updatingItem.id,
        'inventory_seed',
      );
      if (res.status) {
        await loadData(data);
        setSelectedData(undefined);
        showSuccess(res.data?.message ?? 'Success');
      } else {
        showError(res.data?.message ?? 'Something went wrong');
      }
    } else {
      setDisable(true);
      const res = await sendSingleRequest(
        {
          id: selectedData.id,
          ...data,
          weather_condition: selectedData.weather_condition,
        },
        'POST',
        'api/queue/confirm-inventory_seed',
        true,
      );
      if (res.status) {
        await loadData(data);
        setSelectedData(undefined);
        showSuccess(res.data?.message ?? 'Success');
      } else {
        showError(res.data?.message ?? 'Something went wrong');
      }
      setDisable(false);
    }
  };
  const confirmSeeding = async (data: any) => {
    if (!selectedData) return;
    if (updatingItem) {
      const res = await updateQueueItem(data, updatingItem.id, 'seeding');
      if (res.status) {
        await loadData(data);
        setSelectedData(undefined);
        showSuccess(res.data?.message ?? 'Success');
      } else {
        showError(res.data?.message ?? 'Something went wrong');
      }
      return true;
    } else {
      setDisable(true);
      const r = await sendSingleRequest(
        {
          id: selectedData.id,
          ...data,
          weather_condition: selectedData.weather_condition,
        },
        'POST',
        `api/queue/confirm-seeding/${selectedData.crop_type}`,
        true,
      );
      setDisable(false);
      if (!r.status) {
        return r.data;
      } else {
        dispatch(loadSpatStorages());
        dispatch(loadInventories());
        await loadData(data);
        setSelectedData(undefined);
        showSuccess(r.data?.message ?? 'Success');
        return true;
      }
    }
  };
  const confirmHarvest = async (data: any) => {
    if (!selectedData) return;
    if (updatingItem) {
      const res = await updateQueueItem(data, updatingItem.id, 'harvest');
      if (res.status) {
        await loadData(data);
        setSelectedData(undefined);
        showSuccess(res.data?.message ?? 'Success');
      } else {
        showError(res.data?.message ?? 'Something went wrong');
      }
      return res;
    } else {
      setDisable(true);
      const res = await sendSingleRequest(
        {
          id: selectedData.id,
          ...data,
          weather_condition: selectedData.weather_condition,
        },
        'POST',
        `api/queue/confirm-harvest/${selectedData.crop_type}`,
        true,
      );
      if (res.status) {
        dispatch(loadInventories());
        await loadData(data);
        setSelectedData(undefined);
        showSuccess(res.data?.message ?? 'Success');
      }
      setDisable(false);
      return res;
    }
  };
  const confirmBulkItem = async (data: any) => {
    if (!selectedData) return;
    if (updatingItem) {
      const res = await updateQueueItem(
        data,
        updatingItem.id,
        updatingItem.type,
      );
      if (res.status) {
        await loadData(data);
        setSelectedData(undefined);
        showSuccess(res.data?.message ?? 'Success');
      } else {
        showError(res.data?.message ?? 'Something went wrong');
      }
      return true;
    }
    const formData = {
      id: selectedData.id,
      ...data,
      weather_condition: selectedData.weather_condition,
    };
    const suffix = ['bulk_mussel_seed', 'bulk_oyster_seed', 'seeding'].includes(
      selectedData._type,
    )
      ? 'confirm-bulk-seed'
      : selectedData._type === 'bulk_mussel_harvest'
        ? 'confirm-bulk-harvest/mussel'
        : selectedData._type === 'bulk_oyster_harvest'
          ? 'confirm-bulk-harvest/oyster'
          : selectedData._type === 'harvest'
            ? `confirm-bulk-harvest/${selectedData.crop_type.toLowerCase()}`
            : '';
    setDisable(true);
    const r = await sendSingleRequest(
      formData,
      'POST',
      `api/queue/${suffix}`,
      true,
    );
    setDisable(false);
    if (!r.status) {
      showError(r.data?.message ?? 'Something went wrong');
      return r;
    } else {
      dispatch(loadSpatStorages());
      dispatch(loadInventories());
      await loadData(data);
      setSelectedData(undefined);
      showSuccess(r.data?.message ?? 'Success');
      return true;
    }
  };
  const deleteQueueItem = async (data: any = undefined) => {
    if (!data && !deletingItem) return;
    setDisable(true);
    const res = await sendSingleRequest(
      data ?? deletingItem,
      'DELETE',
      'api/queue/entity',
      true,
    );
    setDisable(false);
    setDeletingItem(undefined);
    if (res.status) {
      showSuccess('Deleted successfully');
      loadData(data);
    } else {
      showError(
        res.data?.message ??
          translate(lang, 'Failed to delete %s', 'validate data'),
      );
    }
  };
  const updateQueueItem = async (data: any, item_id: number, type: any) => {
    setDisable(true);
    const res = await sendSingleRequest(
      { data, type, item_id },
      'PUT',
      'api/queue/entity',
      true,
    );
    if (res.status) {
      setUpdatingItem(undefined);
    }
    setDisable(false);

    return res;
  };
  const clickQueueItem = async (
    item: IQueueItem,
    isUpdate: boolean = false,
  ) => {
    if (item.type.startsWith('paper_')) {
      history.push(`/queue/${item.id}`);
      return;
    }

    setUpdatingItem(undefined);

    if (!isUpdate && (item.type === 'seeding' || item.type === 'harvest')) {
      let ms = queueData?.find(
        x =>
          x.type === 'seeding' &&
          x.line.id === item.line.id &&
          x.date < item.date,
      );
      let mh = queueData?.find(
        x =>
          x.type === 'harvest' &&
          x.line.id === item.line.id &&
          x.date < item.date,
      );
      if (!ms || (mh && mh.date < ms.date)) ms = mh;
      if (!!ms) {
        setWarning(
          <WarningBox
            onClick={() => {
              setSelectedData(undefined);
              setTimeout(() => {
                const q = queueData?.find(x => x.id === ms?.id);
                if (q) clickQueueItem(q);
              }, 300);
            }}
            lang={lang}
          />,
        );
      } else {
        setWarning(undefined);
      }
    } else {
      setWarning(undefined);
    }

    setDisable(true);
    const res = await sendSingleRequest(
      {},
      'GET',
      `api/queue/detail/${item.id}`,
      true,
    );
    if (res.status) {
      setDisable(false);
      setSelectedData(res.data);
      if (isUpdate) {
        setUpdatingItem(item);
      }
    } else {
      dispatch(
        showFeedback({
          message: translate(lang, res.data?.message ?? 'Unknown error'),
          type: 'error',
          isMessage: true,
          duration: -1,
        }),
      );
      setDisable(false);
    }
  };
  const isGrower = SelectIsGrower(selectedData?.lineData?.farm_id);

  return (
    <div className='bg-secondary min-height'>
      <div className='container'>
        <div className='queued-page-header d-flex justify-between align-center'>
          <Title size={5} color='black' align='default' fontWeight={700}>
            {translate(lang, 'Unconfirmed data')}
          </Title>
          <Button
            color='blue'
            size={2}
            width='large'
            type='fill'
            onClick={() => setShowCustom(true)}
            disabled={disable}
          >
            {translate(lang, 'Upload paper form')}
          </Button>
        </div>
        <div className='queued-data-page'>
          {loading ? (
            <Spinner position='local' />
          ) : (
            <div className='queue-items'>
              {queueData?.map((item, i) => (
                <QueueItem
                  key={i}
                  item={item}
                  disabled={disable}
                  onViewLocation={setMapLoc}
                  onViewItem={clickQueueItem}
                  onDeleteItem={setDeletingItem}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {selectedData &&
        (/^bulk_(mussel|oyster)_harvest$/.test(selectedData._type) ? (
          <BulkHarvestModal
            visible={true}
            type={
              selectedData._type
                .replace(/^bulk_|_harvest$/g, '')
                .toUpperCase() as TBusinessType
            }
            data={selectedData}
            onConfirm={() => {}}
            overConfirm={confirmBulkItem}
            onCancel={() => setSelectedData(undefined)}
            confirmID={selectedData.id}
            warningMessage={warning}
          />
        ) : selectedData._type === 'seeding' ? (
          <>
            {!!selectedData.is_catch_spat ? (
              <CatchSpatModal
                visible={true}
                type={selectedData.crop_type}
                title={translate(lang, 'Catch spat')}
                lineData={selectedData.lineData}
                onCancel={() => setSelectedData(undefined)}
                onConfirm={() => {}}
                data={selectedData}
                overConfirm={confirmSeeding}
              />
            ) : isGrower ? (
              <GrowerSeedModal
                visible={true}
                onConfirm={() => {}}
                onClose={() => setSelectedData(undefined)}
                overConfirm={confirmBulkItem}
                data={selectedData}
                lineData={selectedData.lineData}
                warningMessage={warning}
              />
            ) : (
              <SeedingModal
                visible={true}
                title={!updatingItem ? 'Seed the line' : 'Update data'}
                onCancel={() => setSelectedData(undefined)}
                onConfirm={() => {}}
                overConfirm={confirmSeeding}
                data={selectedData}
                confirmBtnLabel={
                  !updatingItem ? 'Seed the line' : 'Update data'
                }
                lineData={selectedData.lineData}
                parentID={
                  !selectedData.parent_id ? undefined : selectedData.parent_id
                }
                warningMessage={warning}
                lineChangeable={true}
              />
            )}
          </>
        ) : selectedData._type === 'assessment' ? (
          <AssessmentModal
            title='Add assessment'
            visible={true}
            onCancel={() => setSelectedData(undefined)}
            onConfirm={() => {}}
            overConfirm={confirmAssessment}
            lineData={selectedData.lineData}
            data={selectedData}
            lineChangeable={true}
          />
        ) : selectedData._type === 'harvest' ? (
          isGrower ? (
            <GrowerHarvestModal
              type={selectedData.crop_type}
              visible={true}
              onConfirm={() => {}}
              onClose={() => setSelectedData(undefined)}
              paramData={selectedData}
              overConfirm={confirmBulkItem}
              confirmID={selectedData.id}
              lineData={selectedData.lineData}
              warningMessage={warning}
            />
          ) : selectedData.is_returned ? (
            <ReturnHarvestModal
              visible={true}
              data={selectedData}
              lineData={selectedData.lineData}
              onClose={() => setSelectedData(undefined)}
              overConfirm={confirmHarvest}
            />
          ) : (
            <HarvestCompleteModal
              title={!updatingItem ? 'Harvest Complete' : 'Update data'}
              visible={true}
              onCancel={() => setSelectedData(undefined)}
              onConfirm={() => {}}
              overConfirm={confirmHarvest}
              data={selectedData}
              lineData={selectedData.lineData}
              warningMessage={warning}
              lineChangeable={true}
            />
          )
        ) : selectedData._type === 'inventory_seed' ? (
          <FloatManageModal
            visible={true}
            title={!updatingItem ? 'Inventory management' : 'Update data'}
            onCancel={() => setSelectedData(undefined)}
            onConfirm={() => {}}
            data={selectedData as any}
            lineData={selectedData.lineData}
            overConfirm={confirmInventorySeed}
            lineChangeable
          />
        ) : selectedData._type === 'maintenance' ? (
          <MaintenanceModal
            visible={true}
            title={!updatingItem ? 'Add maintenance' : 'Update data'}
            onCancel={() => setSelectedData(undefined)}
            onConfirm={() => {}}
            overConfirm={confirmMaintenance}
            data={selectedData}
            lineData={selectedData.lineData}
            confirmBtnLabel={!updatingItem ? 'Confirm' : 'Update data'}
            lineChangeable={true}
          />
        ) : ['bulk_mussel_seed', 'bulk_oyster_seed'].includes(
            selectedData._type,
          ) ? (
          !selectedData.spat_storage_id ? (
            <GrowerSeedModal
              visible={true}
              onConfirm={() => {}}
              onClose={() => setSelectedData(undefined)}
              overConfirm={confirmBulkItem}
              data={selectedData}
              lineData={selectedData.lineData}
              warningMessage={warning}
            />
          ) : (
            <BulkSeedingModal
              data={selectedData}
              visible={true}
              onClose={() => setSelectedData(undefined)}
              batchID={selectedData.spat_storage_id}
              overConfirm={confirmBulkItem}
            />
          )
        ) : selectedData._type === 'tagging' ? (
          <LineTagModal
            visible={true}
            lineData={selectedData.lineData}
            onClose={() => setSelectedData(undefined)}
            onComplete={() => {
              setSelectedData(undefined);
              deleteQueueItem({ type: 'tagging', item_id: selectedData.id });
            }}
          />
        ) : (
          <></>
        ))}
      {!!mapLoc && (
        <MapModal
          title={translate(lang, '_data_entry_location')}
          visible={!!mapLoc}
          location={mapLoc}
          onClose={() => setMapLoc(undefined)}
        />
      )}
      {deletingItem && (
        <ModalComponent
          visible={true}
          disabled={disable}
          onCancel={() => setDeletingItem(undefined)}
          type='delete'
          title={translate(lang, 'Delete')}
          text={translate(lang, '_delete_data')}
          onConfirm={() => deleteQueueItem()}
        />
      )}
      {showCustom && (
        <PaperFormModal
          visible={showCustom}
          onClose={() => setShowCustom(false)}
        />
      )}
    </div>
  );
};

export default QueuedDataPage;
